import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Chip,
  Grow,
  Slide,
  CircularProgress,
  Divider,
  Stack,
  IconButton,
  useTheme,
  alpha
} from '@mui/material';
import { 
  PlayArrow, 
  Pause, 
  Stop, 
  AccessTime as ClockIcon,
  EventNote as NoteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../services/api';

const WorkControls = ({ projectId, onWorkUpdate }) => {
  const [activeWork, setActiveWork] = useState(null);
  const [openStartDialog, setOpenStartDialog] = useState(false);
  const [openPauseDialog, setOpenPauseDialog] = useState(false);
  const [description, setDescription] = useState('');
  const [pauseReason, setPauseReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    checkUserRole();
    checkActiveWork();
  }, []);

  const checkUserRole = async () => {
    try {
      const response = await api.get('/users/me');
      if (response.data) {
        setUserRole(response.data.role_id);
      }
    } catch (error) {
      console.error('Erro ao verificar role do usuário:', error);
    }
  };

  // Se não for admin (1) ou técnico (3), não renderiza nada
  if (userRole && ![1, 3].includes(userRole)) {
    return null;
  }

  const checkActiveWork = async () => {
    try {
      const response = await api.get('/work-records/active');
      setActiveWork(response.data);
    } catch (error) {
      console.error('Erro ao verificar trabalho ativo:', error);
    }
  };

  const handleStartWork = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/work-records/', {
        project_id: projectId,
        description: description
      });
      
      setActiveWork(response.data);
      setOpenStartDialog(false);
      setDescription('');
      
      if (onWorkUpdate) {
        onWorkUpdate();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao iniciar o trabalho');
    } finally {
      setLoading(false);
    }
  };

  const handlePauseWork = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await api.post('/work-pauses/', {
        record_id: activeWork.record_id,
        reason: pauseReason
      });
      
      await checkActiveWork();
      setOpenPauseDialog(false);
      setPauseReason('');
      
      if (onWorkUpdate) {
        onWorkUpdate();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao pausar o trabalho');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeWork = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const activePause = activeWork.pauses.find(p => !p.end_time);
      if (activePause) {
        await api.put(`/work-pauses/${activePause.pause_id}/finish`);
        await checkActiveWork();
        
        if (onWorkUpdate) {
          onWorkUpdate();
        }
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao retomar o trabalho');
    } finally {
      setLoading(false);
    }
  };

  const handleFinishWork = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await api.put(`/work-records/${activeWork.record_id}/finish`);
      setActiveWork(null);
      
      if (onWorkUpdate) {
        onWorkUpdate();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao finalizar o trabalho');
    } finally {
      setLoading(false);
    }
  };

  // Formatar duração do trabalho ativo (se houver)
  const formatDuration = (startTime) => {
    if (!startTime) return '';
    
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}min`;
  };

  return (
    <Box>
      {error && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            borderLeft: `4px solid ${theme.palette.error.main}`,
            borderRadius: '4px'
          }}
        >
          <Typography color="error.main" variant="body2" sx={{ fontWeight: 500 }}>
            {error}
          </Typography>
        </Paper>
      )}

      {activeWork ? (
        <Grow in timeout={300}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              mb: 3,
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark' 
                ? `linear-gradient(to right, ${alpha(theme.palette.primary.dark, 0.1)}, ${alpha(theme.palette.background.paper, 0.7)})`
                : `linear-gradient(to right, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.background.paper, 0.7)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              height: '4px', 
              backgroundColor: activeWork.status === 'in_progress' ? theme.palette.primary.main : theme.palette.warning.main,
              opacity: 0.8
            }} />
            
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mr: 2 }}>
                  Trabalho em Andamento
                </Typography>
                <Chip 
                  label={activeWork.status === 'in_progress' ? 'Em progresso' : 'Pausado'} 
                  color={activeWork.status === 'in_progress' ? 'primary' : 'warning'}
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
                {activeWork.start_time && (
                  <Chip 
                    icon={<ClockIcon fontSize="small" />}
                    label={formatDuration(activeWork.start_time)} 
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                )}
              </Box>
              
              <Box 
                sx={{ 
                  p: 2, 
                  borderRadius: '8px', 
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 1,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1
                  }}
                >
                  <NoteIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <span>{activeWork.description}</span>
                </Typography>
                
                {activeWork.status === 'paused' && activeWork.pauses && activeWork.pauses.length > 0 && (
                  <Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="warning.main" sx={{ mt: 1, fontWeight: 500 }}>
                      Motivo da pausa atual: {activeWork.pauses.find(p => !p.end_time)?.reason}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {activeWork.status === 'in_progress' ? (
                  <>
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<Pause />}
                      onClick={() => setOpenPauseDialog(true)}
                      disabled={loading}
                      sx={{ 
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontWeight: 500,
                        px: 3,
                        '&:hover': {
                          boxShadow: theme.shadows[8]
                        }
                      }}
                    >
                      Pausar
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Stop />}
                      onClick={handleFinishWork}
                      disabled={loading}
                      sx={{ 
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontWeight: 500,
                        px: 3,
                        '&:hover': {
                          boxShadow: theme.shadows[8]
                        }
                      }}
                    >
                      Finalizar
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrow />}
                    onClick={handleResumeWork}
                    disabled={loading}
                    sx={{ 
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 500,
                      px: 3,
                      '&:hover': {
                        boxShadow: theme.shadows[8]
                      }
                    }}
                  >
                    Retomar
                  </Button>
                )}
                
                {loading && (
                  <CircularProgress size={24} sx={{ ml: 2 }} />
                )}
              </Box>
            </Stack>
          </Paper>
        </Grow>
      ) : (
        <Button
          variant="contained"
          color="primary"
          startIcon={<PlayArrow />}
          onClick={() => setOpenStartDialog(true)}
          disabled={loading}
          size="large"
          sx={{ 
            textTransform: 'none',
            borderRadius: '8px',
            fontWeight: 500,
            px: 4,
            py: 1.5,
            boxShadow: theme.shadows[2],
            '&:hover': {
              boxShadow: theme.shadows[8]
            }
          }}
        >
          Iniciar Trabalho
        </Button>
      )}

      {/* Diálogo para iniciar trabalho */}
      <Dialog 
        open={openStartDialog} 
        onClose={() => setOpenStartDialog(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        TransitionProps={{
          direction: 'up'
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Iniciar Trabalho</Typography>
            <IconButton onClick={() => setOpenStartDialog(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="O que será feito?"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descreva detalhadamente o trabalho que será realizado..."
            InputProps={{
              sx: {
                borderRadius: '8px'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpenStartDialog(false)}
            sx={{ 
              textTransform: 'none',
              borderRadius: '8px'
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleStartWork} 
            variant="contained" 
            disabled={!description.trim() || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
            sx={{ 
              textTransform: 'none',
              borderRadius: '8px',
              px: 3
            }}
          >
            {loading ? 'Iniciando...' : 'Iniciar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para pausar trabalho */}
      <Dialog 
        open={openPauseDialog} 
        onClose={() => setOpenPauseDialog(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        TransitionProps={{
          direction: 'up'
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Pausar Trabalho</Typography>
            <IconButton onClick={() => setOpenPauseDialog(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Motivo da pausa"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={pauseReason}
            onChange={(e) => setPauseReason(e.target.value)}
            placeholder="Descreva o motivo para interromper temporariamente o trabalho..."
            InputProps={{
              sx: {
                borderRadius: '8px'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpenPauseDialog(false)}
            sx={{ 
              textTransform: 'none',
              borderRadius: '8px'
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handlePauseWork} 
            variant="contained" 
            color="warning"
            disabled={!pauseReason.trim() || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Pause />}
            sx={{ 
              textTransform: 'none',
              borderRadius: '8px',
              px: 3
            }}
          >
            {loading ? 'Pausando...' : 'Pausar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkControls; 